import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
} from '@mui/material'
import { useMemo, useState, type MouseEvent } from 'react'
import { MultiSelectOptionChip } from 'objects/property-forms/multi-select/MultiSelectOptionChip.tsx'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { SelectPropertyPopoverSearch } from '@/objects/property-forms/select-1/SelectPropertyPopoverSearch.tsx'
import { propertyFormConfig } from 'objects/property-forms/PropertyFormConfig.tsx'
import type { PropertyOptionModel } from '@/generated/graphql.ts'

export interface MultiSelectPropertyValuePopoverProps {
  selectedOptions: PropertyOptionModel[]
  availableOptions: PropertyOptionModel[]
  onOptionSelected: (option: PropertyOptionModel) => void
  onChipOptionsClick?: (e: MouseEvent, option: PropertyOptionModel) => void
  onChipDeselect?: (option: PropertyOptionModel) => void
  /** When provided, enables creating a new option from the search text. */
  newOptionFactory?: (value: string) => PropertyOptionModel
}

const config = {
  leftIndent: '1rem',
  maxRows: 8,
  listPaddingPx: 2,
  optionHeightPx: 28,
}

export function MultiSelectPropertyValuePopover({
  selectedOptions,
  availableOptions,
  onOptionSelected,
  onChipOptionsClick,
  onChipDeselect,
  newOptionFactory,
}: MultiSelectPropertyValuePopoverProps) {
  const [searchText, setSearchText] = useState('')
  const [newOption, setNewOption] = useState<PropertyOptionModel | undefined>(() =>
    newOptionFactory?.(''),
  )
  if (newOption) newOption.value = searchText.trim()

  const filteredOptions = useMemo(() => {
    const lower = searchText.toLowerCase()
    return availableOptions.filter((opt) => opt.value.toLowerCase().includes(lower))
  }, [searchText, availableOptions])

  function onNewOptionCreatedAndSelected() {
    if (!newOption || !newOptionFactory) return
    onOptionSelected(newOption)
    setSearchText('')
    setNewOption(newOptionFactory(''))
  }

  const shouldAddNewOptionChip =
    newOption &&
    newOption.value &&
    !availableOptions.some((opt) => opt.value.toLowerCase() === searchText.trim().toLowerCase())

  const listMaxHeightPx = config.maxRows * config.optionHeightPx + config.listPaddingPx * 2

  return (
    <Box sx={{ width: 240, backgroundColor: 'transparent' }}>
      {selectedOptions.length > 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              px: 1.5,
              py: 1,
            }}
          >
            {selectedOptions.map((opt) => (
              <MultiSelectOptionChip
                key={opt.id}
                option={opt}
                alwaysShowActions
                {...(onChipOptionsClick && { onOptionsClick: (e) => onChipOptionsClick(e, opt) })}
                {...(onChipDeselect && { onDeselectClick: () => onChipDeselect(opt) })}
              />
            ))}
          </Box>
          <Divider />
        </>
      )}

      <SelectPropertyPopoverSearch
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={newOptionFactory ? 'Search or add options' : 'Search options'}
        {...(newOptionFactory && { onSubmit: onNewOptionCreatedAndSelected })}
      />

      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="left"
        paddingLeft={config.leftIndent}
      >
        Select options
      </Typography>

      <List
        sx={{
          pt: `${config.listPaddingPx}px`,
          pb: `${config.listPaddingPx}px`,
          maxHeight: listMaxHeightPx,
          overflowY: 'auto',
        }}
      >
        {filteredOptions.map((option) => (
          <ListItem
            sx={{ height: config.optionHeightPx }}
            key={option.id}
            disablePadding
            secondaryAction={
              onChipOptionsClick && (
                <IconButton
                  sx={{ height: propertyFormConfig.propHeight, padding: 0 }}
                  edge="end"
                  size="small"
                  onClick={(e) => onChipOptionsClick(e, option)}
                >
                  <MoreVertIcon />
                </IconButton>
              )
            }
          >
            <ListItemButton
              sx={{
                height: '100%',
                padding: '4px',
                pl: config.leftIndent,
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => onOptionSelected(option)}
            >
              <MultiSelectOptionChip option={option} />
            </ListItemButton>
          </ListItem>
        ))}

        {shouldAddNewOptionChip && newOption && (
          <ListItem disablePadding>
            <ListItemButton onClick={onNewOptionCreatedAndSelected}>
              <ListItemIcon sx={{ minWidth: 0, mr: 1, gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Create
                </Typography>
                <MultiSelectOptionChip option={newOption} />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  )
}
